import _ from "lodash";
import * as React from "react";
import { Link } from "react-router-dom";
import * as Comp from "../../../../components";
import { ComponentWithValidatedForm } from "../../../../components/validation";
import { Paths } from "../../../../constants";
import * as Forms from "../../../../forms";
import * as M from "../../../../models";
import { Api } from "../../../../services/Api";
import { AbstractTypeMediaEditor } from "../../../abstracts/composer/components/AbstractTypeMediaEditor";

interface OwnProps {
    api: Api;
    abstract: M.Abstract.Abstract;
    eventId: number;
    type: M.Abstract.AbstractType;
    onSubmit: (model: Forms.AdminAbstract.AbstractUploadsData, body: any) => void;
    onMediaDeleted: (abstractMediaId: number) => void;
    subscribeToMediaProcessing: (mediaId: number) => void;
    updateAbstractMedia: (media: M.Abstract.AbstractMedia) => void;
}

interface OwnState {
    uploadStatus: number[];
}

export class UploadsForm extends ComponentWithValidatedForm<OwnProps, OwnState> {

    public constructor(props: any) {
        super(props);
        const formModel = new Forms.AdminAbstract.AbstractUploads();
        this.state = {
            ...this.getInitialState(formModel),
            uploadStatus: [],
        };
    }

    public componentDidMount() {
        this.setFormData(this.props.type, this.props.abstract);
    }

    public componentDidUpdate(prevProps: Readonly<OwnProps>, prevState: Readonly<OwnState>) {
    }

    public setFormData(type: M.Abstract.AbstractType, abstract: M.Abstract.Abstract) {
        const formModel = this.getFormModel() as Forms.AdminAbstract.AbstractUploads;
        formModel.data.typeMedia = type.typeMedia.map((tm, i) => {
            return {
                description: tm.description,
                id: tm.id,
                index: i,
                label: tm.label,
                labelHint: tm.labelHint,
                maxUploads: tm.maxUploads,
                maxSize: tm.maxSize,
                minUploads: tm.minUploads,
                media: abstract && abstract.media
                    .filter((m) => m.abstractTypeMediaId === tm.id)
                    .map((m, j) => ({
                        caption: m.caption,
                        deleted: false,
                        embedUrl: m.embedUrl,
                        fileName: m.fileName,
                        height: m.height,
                        id: m.abstractMediaId,
                        index: j,
                        isImage: m.isImage,
                        isProcessing: m.isProcessing,
                        isVideo: m.isVideo,
                        mediaId: m.id,
                        tags: m.tags
                            ? m.tags.map((x) => ({
                                id: x.id,
                                label: x.name,
                                name: x.name,
                                value: x.id,
                            }))
                            : [],
                        thumbMd: m.thumbMd,
                        thumbSm: m.thumbSm,
                        title: m.title,
                        typeId: m.typeId,
                        url: m.url,
                        width: m.width,
                    })) || [],
                mediaType: tm.mediaType,
                mediaTypeExtns: tm.mediaTypeExtns,
                mediaTypeId: tm.mediaTypeId,
                promptCaption: tm.promptCaption,
                promptKeywords: tm.promptKeywords,
                promptTitle: tm.promptTitle,
                requiredAfterAccept: tm.requiredAfterAccept,
                requiredForSubmit: tm.requiredForSubmit,
            };
        });

        // add empty media
        for (const t of formModel.data.typeMedia) {
            if (!t.media.length)
                t.media.push({ ...Forms.Abstract.DefaultAbstractMedia });
        }

        this.setState({
            ...this.state,
            formModel,
        });
    }

    public updateMediaForm(media: M.Abstract.AbstractMedia) {
        const formModel = this.getFormModel() as Forms.AdminAbstract.AbstractUploads;
        for (const tm of formModel.data.typeMedia) {
            const mediaForm = _.find(tm.media, (m) => m.mediaId === media.id);
            if (mediaForm) {
                mediaForm.caption = mediaForm.caption || media.caption;
                mediaForm.embedUrl = media.embedUrl;
                mediaForm.fileName = media.fileName;
                mediaForm.height = media.height;
                mediaForm.id = media.abstractMediaId;
                mediaForm.isImage = media.isImage;
                mediaForm.isProcessing = media.isProcessing;
                mediaForm.isVideo = media.isVideo;
                mediaForm.tags = mediaForm.tags.length
                    ? mediaForm.tags
                    : media.tags.map((x) => ({
                        id: x.id,
                        label: x.name,
                        name: x.name,
                        value: x.id,
                    }));
                mediaForm.title = media.title;
                mediaForm.typeId = media.typeId;
                mediaForm.url = media.url;
                mediaForm.width = media.width;
            }
        }

        this.setState({
            ...this.state,
            formModel,
        });
    }

    public render() {
        const formModel = this.getFormModel() as Forms.AdminAbstract.AbstractUploads;
        const cancelLink = Paths.AdminAbstractDetails(this.props.eventId, this.props.abstract.guid);

        if (!this.props.type.typeMedia.length)
            return <>
                <p>No uploads required for type <strong>{this.props.type.name}</strong>.</p>
                <ul>
                    <li><Link to={Paths.AdminAbstractEdit(this.props.eventId, this.props.abstract.guid)}>Edit authorship &raquo;</Link></li>
                    <li><Link to={Paths.AdminAbstractEditDetails(this.props.eventId, this.props.abstract.guid)}>Edit details &raquo;</Link></li>
                    <li><Link to={Paths.AdminAbstractEditPermissions(this.props.eventId, this.props.abstract.guid)}>Edit permissions &raquo;</Link></li>
                    <li><Link to={Paths.AdminAbstractEditSettings(this.props.eventId, this.props.abstract.guid)}>Edit admin settings &raquo;</Link></li>
                </ul>
            </>;

        return <Comp.Validation.ValidatedForm
            formComponent={this}
            model={this.state.formModel.data}
            onCustomValidation={() => this.handleValidation()}
            onValidSubmit={async (model) => this.handleSubmit(model)}
            schema={this.state.formModel.rules}
            ref={(ref: any) => this.form = ref}
        >
            {formModel.data.typeMedia.map((t, i) => (
                <AbstractTypeMediaEditor
                    key={i}
                    abstractGuid={this.props.abstract ? this.props.abstract.guid : ""}
                    abstractTypeMedia={t}
                    api={this.props.api}
                    formComponent={this}
                    parentFieldName={`typeMedia[${t.index}]`}
                    type={this.props.type}
                    onMediaDeleted={(abstractMediaId) => this.props.onMediaDeleted(abstractMediaId)}
                    onUploadCompleted={(abstractTypeMediaId) => this.updateUploadStatus(abstractTypeMediaId, true)}
                    onUploadStarted={(abstractTypeMediaId) => this.updateUploadStatus(abstractTypeMediaId, false)}
                    subscribeToMediaProcessing={this.props.subscribeToMediaProcessing}
                    updateAbstractMedia={this.props.updateAbstractMedia}
                />))}

            <Comp.Editors.SubmitField cancelLink={cancelLink} label="Save" showProcessing={this.state.formProcessing} />

        </Comp.Validation.ValidatedForm>;
    }

    private handleSubmit(model: Forms.AdminAbstract.AbstractUploadsData) {
        const mediaForms = model.typeMedia
            .map((tm) => tm.media)
            .reduce((a, b) => a.concat(b))
            .filter((m) => !(m.id <= 0 && m.deleted) && m.id > 0);

        const body = {
            abstractId: model.abstractId,
            guid: this.props.abstract ? this.props.abstract.guid : undefined,
            media: mediaForms.map((m, i) => ({
                abstractMediaId: m.id,
                caption: m.caption,
                deleted: m.deleted,
                mediaId: m.mediaId,
                tags: (m.tags || []).map((t: M.Tag.Tag) => ({
                    id: t.id,
                    name: t.name,
                })),
                title: m.title,
            })),
        };

        this.props.onSubmit(model, body);
    }

    private handleValidation() {
        // do not validate required uploads here.
        return M.Abstract.validateUploadsStep(this.state.formModel, this.props.type !== undefined && this.props.type.uploadDeadline !== null);
    }

    private updateUploadStatus(abstractTypeMedia: number, isDone: boolean) {
        let uploadStatus = [...this.state.uploadStatus];
        if (isDone) {
            uploadStatus = uploadStatus.filter((x) => x !== abstractTypeMedia);
        }
        else {
            if (uploadStatus.indexOf(abstractTypeMedia) === -1)
                uploadStatus.push(abstractTypeMedia);
        }

        this.setState({ ...this.state, uploadStatus });
    }
}



// WEBPACK FOOTER //
// ./src/containers/adminAbstracts/edit/components/UploadsForm.tsx